import { useCallback, useState } from "react";

/**
 * Custom hook for user preferences that persist across browser sessions and tabs.
 * Uses localStorage for storage.
 *
 * @param key - The storage key (will be prefixed with component namespace)
 * @param defaultValue - The default value if nothing is stored
 * @param namespace - The namespace prefix for the storage key
 * @returns A tuple of [value, setValue] similar to useState
 */
export function usePersistedPreference<T>(
  key: string,
  defaultValue: T,
  namespace = "app",
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(`${namespace}.preferences.${key}`);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setPersistedValue = useCallback(
    (newValue: T) => {
      setValue(newValue);
      try {
        localStorage.setItem(
          `${namespace}.preferences.${key}`,
          JSON.stringify(newValue),
        );
      } catch (e) {
        console.error("Failed to save preference:", e);
      }
    },
    [key, namespace],
  );

  return [value, setPersistedValue];
}

/**
 * Custom hook for session filters that persist only in the current tab session.
 * Uses sessionStorage for storage.
 *
 * @param key - The storage key (will be prefixed with component namespace)
 * @param defaultValue - The default value if nothing is stored
 * @param namespace - The namespace prefix for the storage key
 * @returns A tuple of [value, setValue] similar to useState
 */
export function useSessionFilter<T>(
  key: string,
  defaultValue: T,
  namespace = "app",
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(`${namespace}.filters.${key}`);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setSessionValue = useCallback(
    (newValue: T) => {
      setValue(newValue);
      try {
        sessionStorage.setItem(
          `${namespace}.filters.${key}`,
          JSON.stringify(newValue),
        );
      } catch (e) {
        console.error("Failed to save filter:", e);
      }
    },
    [key, namespace],
  );

  return [value, setSessionValue];
}
